import { ApolloError, gql, useQuery } from '@apollo/client';
import IconEdit from '@apollo/icons/small/IconEdit.svg';
import IconInfo from '@apollo/icons/small/IconInfo.svg';
import IconLock from '@apollo/icons/small/IconLock.svg';
import IconTrash from '@apollo/icons/small/IconTrash.svg';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Group,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Table,
  TableContainer,
  TableSortButton,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import classnames from 'classnames';
import { orderBy, partition } from 'lodash';
import React, { useMemo, useState } from 'react';
import {
  Link as ReactRouterLink,
  useHistory,
  useLocation,
} from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { URLModal } from 'src/components/common/URLModal';
import { ContactSupportLink } from 'src/components/ContactSupportCTAs';
import { FullNameColumn } from 'src/components/fullNameColumn/FullNameColumn';
import { PermissionBlock } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useIdentity } from 'src/hooks/useIdentity';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import Config from 'src/lib/config';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { UserPermission } from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { GraphRef } from '../../hooks/useGraphRef';

import {
  AddHiddenGraphMemberModal,
  AddHiddenGraphMemberModalMembershipFragment,
} from './addHiddenGraphMemberModal/AddHiddenGraphMemberModal';
import {
  RemoveGraphRoleOverrideModal,
  RemoveGraphRoleOverrideModalMembershipFragment,
} from './removeGraphRoleOverrideModal/RemoveGraphRoleOverrideModal';
import { UpdateGraphRoleModal } from './updateGraphRoleModal/UpdateGraphRoleModal';

enum SortableField {
  email,
  graphRole,
  orgRole,
}

const AccessPageQuery = gql`
  query AccessPageQuery($graphId: ID!) {
    service(id: $graphId) {
      id
      hiddenFromUninvitedNonAdminAccountMembers
      roleOverrides {
        role
        user {
          id
        }
      }
      permissions: roles {
        canManageAccess
      }
      account {
        id
        availableRoles
        memberships {
          role: permission
          user {
            id
            avatarUrl
            fullName
            email
            githubUsername
          }
          account {
            id
          }
          ...RemoveGraphRoleOverrideModalMembershipFragment
          ...AddHiddenGraphMemberModalMembershipFragment
        }
        permissions: roles {
          canQueryMembers
          canManageMembers
        }
      }
      createdBy {
        id
        ... on User {
          id
          fullName
        }
      }
    }
    canBypassPlanRestrictions
  }
  ${RemoveGraphRoleOverrideModalMembershipFragment}
  ${AddHiddenGraphMemberModalMembershipFragment}
`;

type ActionModalData = {
  membership: GraphQLTypes.AccessPageQuery_service_account_memberships;
  type: 'update-graph-role' | 'remove-member' | 'add-member';
} | null;
const ActionModals = ({
  data,
  ...props
}: {
  data: ActionModalData;
  onClose: () => void;
  graphId: string;
  accountId: string;
  isSelf: boolean;
  graphRole: GraphQLTypes.UserPermission | undefined;
  availableRoles: GraphQLTypes.UserPermission[];
}) => {
  if (!data) return null;
  switch (data.type) {
    case 'remove-member':
      return (
        <RemoveGraphRoleOverrideModal
          isOpen
          membership={data.membership}
          {...props}
        />
      );
    case 'update-graph-role':
      return (
        <UpdateGraphRoleModal isOpen membership={data.membership} {...props} />
      );
    case 'add-member':
      return null;
    default:
      assertUnreachable(data.type);
  }
};

export const AccessPage = ({ graphRef }: { graphRef: GraphRef }) => {
  const [currentAccountId] = useCurrentAccountId();
  const location = useLocation();
  const history = useHistory();
  const { me, meLoading } = useIdentity();
  const { data, loading } = useQuery<
    GraphQLTypes.AccessPageQuery,
    GraphQLTypes.AccessPageQueryVariables
  >(AccessPageQuery, {
    variables: {
      graphId: graphRef.graphId,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });

  const [sortAscending, setSortAscending] = useState(true);
  const [selectedField, setSelectedField] = useState(SortableField.graphRole);
  const [modalData, setModalData] = useState<ActionModalData>(null);

  const roleOverrides = data?.service?.roleOverrides;
  const roleOverrideByUserId: {
    [userId in string]?: GraphQLTypes.UserPermission;
  } = useMemo(
    () =>
      Object.fromEntries(
        roleOverrides?.map(({ user, role }) => [user.id, role]) ?? [],
      ),
    [roleOverrides],
  );

  const service = data?.service;
  const hiddenFromUninvitedNonAdminAccountMembers =
    service?.hiddenFromUninvitedNonAdminAccountMembers;
  const [memberships, notMembers] = useMemo(
    () =>
      partition(
        service?.account?.memberships?.filter(
          (membership) => membership.role !== UserPermission.BILLING_MANAGER,
        ),
        (membership) => {
          return !!(
            !hiddenFromUninvitedNonAdminAccountMembers ||
            roleOverrideByUserId[membership.user.id] ||
            membership.role === UserPermission.ORG_ADMIN
          );
        },
      ),
    [hiddenFromUninvitedNonAdminAccountMembers, roleOverrideByUserId, service],
  );

  const sortedTableData = useMemo(() => {
    return orderBy(
      memberships,
      (member) => {
        switch (selectedField) {
          case SortableField.email:
            return member.user.email?.toLowerCase();
          case SortableField.orgRole:
            if (member.user.id === service?.createdBy?.id) return 1;
            if (Config.roleIsGraphApiKeyOnly(member.role)) return -1;
            return Config.roleOrder.indexOf(member.role);
          case SortableField.graphRole: {
            if (member.user.id === service?.createdBy?.id)
              return Config.roleOrder.length;

            const graphRole = roleOverrideByUserId[member.user.id];
            const index =
              graphRole && !Config.roleIsGraphApiKeyOnly(graphRole)
                ? Config.roleOrder.indexOf(graphRole)
                : -1;

            return index === -1 ? graphRole : index;
          }
          default:
            throw assertUnreachable(selectedField);
        }
      },
      [sortAscending ? 'asc' : 'desc'],
    );
  }, [
    memberships,
    roleOverrideByUserId,
    selectedField,
    service?.createdBy?.id,
    sortAscending,
  ]);

  const [actionError, setActionError] = useState<ApolloError | null>(null);

  if (!data || loading || !service || meLoading || currentPlan.loading) {
    return <Loading />;
  }

  if (me?.__typename !== 'User') {
    throw Error(
      `Must be logged in as User to see Graph Settings. Current identity = ${me?.__typename}`,
    );
  }

  if (!service.account?.permissions?.canQueryMembers) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>
            <PermissionBlock />
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const account = service.account;

  function handleSortFieldSelect(field: SortableField, nextAscending: boolean) {
    setSelectedField(field);
    setSortAscending(nextAscending);
  }
  const userRoles = !!currentPlan.currentPlan?.userRoles;

  return (
    <>
      {/** this modal is managed here because we need the selected user */}
      <ActionModals
        data={modalData}
        onClose={() => setModalData(null)}
        graphId={service.id}
        isSelf={modalData?.membership.user.id === me.id}
        accountId={account.id}
        graphRole={
          modalData
            ? roleOverrideByUserId[modalData.membership.user.id]
            : undefined
        }
        availableRoles={account.availableRoles}
      />
      <URLModal name={Config.modals.addHiddenGraphMember}>
        {({ isOpen, closeModal }) => (
          <AddHiddenGraphMemberModal
            isOpen={isOpen}
            members={notMembers}
            onClose={closeModal}
            graphId={service.id}
            accountId={account.id}
            availableRoles={account.availableRoles}
          />
        )}
      </URLModal>

      {actionError && (
        <Modal isOpen size="lg" onClose={() => setActionError(null)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Error</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <div className="w-full rounded bg-red-lightest px-4 py-3 text-base leading-normal text-red-dark">
                <div>An error occurred: {actionError.message}</div>
                <div>Please try again</div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="primary" onClick={() => setActionError(null)}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      {!userRoles ? (
        <Alert status="info">
          Your organization's plan does not allow you to configure permission
          access at the graph level. Please{' '}
          <ContactSupportLink>contact support</ContactSupportLink> to learn more
          about our available plans and upgrade your subscription.
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            {sortedTableData.length ?? 0}{' '}
            {format.pluralize(sortedTableData.length ?? 0, 'member')}
          </CardTitle>
          <CardDescription>
            Organization members will have access based on their org role. Org
            roles can be overridden with a graph level role.{' '}
          </CardDescription>
          <ButtonGroup>
            {currentAccountId && (
              <Button
                variant="secondary"
                as={ReactRouterLink}
                to={orgRoutes.members.location({ orgId: currentAccountId })}
              >
                View organization members
              </Button>
            )}

            <Tooltip
              label={
                userRoles
                  ? 'All of your organization members have already been added to this graph.'
                  : 'Your account does not have access to user role management.'
              }
              disabled={notMembers.length > 0}
            >
              <Button
                isDisabled={notMembers.length === 0}
                variant="secondary"
                onClick={() => {
                  history.push(
                    setOverlayVisibleLocation({
                      location,
                      overlayName: Config.modals.addHiddenGraphMember,
                    }),
                  );
                }}
              >
                Add
              </Button>
            </Tooltip>
          </ButtonGroup>
        </CardHeader>
        <CardBody>
          <TableContainer>
            <Table
              sortBy={selectedField}
              sortOrder={sortAscending ? 'asc' : 'desc'}
              onRequestSort={({ sortBy, sortOrder }) => {
                handleSortFieldSelect(sortBy, sortOrder === 'asc');
              }}
            >
              <Thead>
                <Tr>
                  <Th>
                    <Group>
                      Members
                      <TableSortButton attribute={SortableField.email} />
                    </Group>
                  </Th>
                  <Th className="w-[30%]">
                    <Group>
                      Organization role
                      <Tooltip
                        interactive
                        label={
                          <>
                            You can read about the different roles in the{' '}
                            <Link to={DocsRouter.path('UserRoles')} isExternal>
                              Studio documentation.
                            </Link>
                          </>
                        }
                      >
                        <IconInfo />
                      </Tooltip>
                      <TableSortButton attribute={SortableField.orgRole} />
                    </Group>
                  </Th>
                  <Th className="w-[30%]">
                    <Group>
                      Graph role
                      <Tooltip
                        interactive
                        label={
                          userRoles ? (
                            <>
                              You can read about the different roles in the{' '}
                              <Link
                                to={DocsRouter.path('GraphRoles')}
                                isExternal
                              >
                                Studio documentation.
                              </Link>
                            </>
                          ) : (
                            <>
                              This is an Enterprise feature. Please{' '}
                              <ContactSupportLink>
                                get in touch with us
                              </ContactSupportLink>{' '}
                              if you need access to this.
                            </>
                          )
                        }
                      >
                        <IconInfo />
                      </Tooltip>
                      <TableSortButton attribute={SortableField.graphRole} />
                    </Group>
                  </Th>
                  <Th>
                    <span className="sr-only">Actions</span>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedTableData.map((membership) => {
                  const { user, role } = membership;
                  const graphRole = roleOverrideByUserId[user.id];
                  const graphRoleDisplay =
                    (graphRole && Config.roleNameByRoleEnum[graphRole]) ??
                    graphRole;

                  return (
                    <Tr key={user.id}>
                      <Td>
                        <FullNameColumn
                          ghUsername={user.githubUsername}
                          accountId={account.id}
                          avatarUrl={user.avatarUrl}
                          fullName={user.fullName}
                          email={user.email}
                          label={
                            service.createdBy?.id === user.id ? 'CREATOR' : ''
                          }
                        />
                      </Td>
                      <Td
                        className={classnames({
                          'text-placeholder line-through': graphRole,
                        })}
                      >
                        {Config.roleNameByRoleEnum[role] ?? role}
                      </Td>
                      <Td>
                        <div className="truncate">{graphRoleDisplay}</div>
                      </Td>
                      <Td isActionsMenu>
                        {(userRoles || data.canBypassPlanRestrictions) &&
                        (account.permissions?.canManageMembers ||
                          service.permissions?.canManageAccess) ? (
                          <MembershipActionsMenu
                            membership={membership}
                            setModalData={setModalData}
                            service={service}
                            userRoles={
                              currentPlan.currentPlan?.userRoles || false
                            }
                            canBypassPlanRestrictions={
                              data.canBypassPlanRestrictions
                            }
                            roleOverrideByUserId={roleOverrideByUserId}
                          />
                        ) : null}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>
    </>
  );
};

interface MembershipActionsMenuProps {
  setModalData: React.Dispatch<React.SetStateAction<ActionModalData>>;
  membership: GraphQLTypes.AccessPageQuery_service_account_memberships;
  service: GraphQLTypes.AccessPageQuery_service;
  userRoles: boolean;
  canBypassPlanRestrictions: boolean;
  roleOverrideByUserId: {
    [userId in string]?: GraphQLTypes.UserPermission;
  };
}

const MembershipActionsMenu = ({
  membership,
  setModalData,
  service,
  userRoles,
  canBypassPlanRestrictions,
  roleOverrideByUserId,
}: MembershipActionsMenuProps) => {
  if (
    (!userRoles && !canBypassPlanRestrictions) ||
    !service.permissions?.canManageAccess
  ) {
    return null;
  }
  if (membership.role === GraphQLTypes.UserPermission.ORG_ADMIN) {
    return (
      <Tooltip label="Cannot edit roles for admins">
        <IconButton
          isDisabled
          variant="hidden"
          aria-label="Cannot edit roles for admins"
          icon={<IconLock />}
        />
      </Tooltip>
    );
  }
  return (
    <>
      <Tooltip
        label={
          <>
            Set a role for <em>{membership.user.fullName}</em>
          </>
        }
      >
        <IconButton
          variant="hidden"
          aria-label={`Set a role for ${membership.user.fullName}`}
          icon={<IconEdit />}
          onClick={() =>
            setModalData({ membership, type: 'update-graph-role' })
          }
        />
      </Tooltip>
      <Tooltip
        label={
          <>
            Remove <em>{membership.user.fullName}</em>’s graph role override
          </>
        }
      >
        <IconButton
          variant="hidden"
          isDisabled={!(membership.user.id in roleOverrideByUserId)}
          aria-label="Remove graph role override"
          className={classnames(
            membership.user.id in roleOverrideByUserId
              ? 'visible'
              : 'invisible',
          )}
          icon={<IconTrash />}
          onClick={() => setModalData({ membership, type: 'remove-member' })}
        />
      </Tooltip>
    </>
  );
};
